import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Box, Typography, Grid, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { School } from '@mui/icons-material';
import apiClient from '../../config/axios';
import { useAuth } from '../../contexts/AuthContext';
import ModalResult from '../../components/ModalResult';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import CourseCard from '../courses/components/CourseCard';

const TrialLessons = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('success'); // 'success' | 'error'
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [enrollForm, setEnrollForm] = useState({
    paymentMethod: 'monthly',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Lezioni di prova
      // minLevel = 0
      // isMembershipRequired = false
      // costo 0
      const coursesRes = await apiClient.get('/api/courses?status=published');
      const trialCourses = coursesRes.data.filter(course => 
        course.minLevel === 0 && course.isMembershipRequired === false && (course.pricing.monthly === 0 && course.pricing.annual1 === 0 && course.pricing.annual3 === 0)
      );
      setCourses(trialCourses);
    } catch (fetchError) {
      console.error('Errore durante il caricamento delle lezioni di prova:', fetchError);
      setError('Errore nel caricamento delle lezioni di prova');
      setModalType('error');
      setModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = (course) => {
    setSelectedCourse(course);
    setEnrollForm({
      paymentMethod: 'monthly',
    });
    setEnrollDialogOpen(true);
  };

  const handleEnrollSubmit = async () => {
    // Naviga al workflow di iscrizione passando i dati del corso selezionato
    if (!selectedCourse) return;
    const courseData = {
      _id: selectedCourse._id,
      name: selectedCourse.name,
      description: selectedCourse.description,
      courseStartDate: selectedCourse.startDate,
      courseEndDate: selectedCourse.endDate,
      enrollmentStartDate: selectedCourse.startDate, // una data qualsiasi
      amount: calculatePaymentAmount(enrollForm.paymentMethod),
      paymentMethod: enrollForm.paymentMethod,
      venue: selectedCourse.venue,
      pricing: selectedCourse.pricing,
    };
    setEnrollDialogOpen(false);
    navigate('/course-enrollment-checkout', { state: { courseData } });
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSuccess(null);
    setError(null);
  };

  const isEnrolled = (courseId) => {
    return Array.isArray(user?.enrollments) && user.enrollments.some((enrollment) => enrollment.courseId === courseId);
  };

  const calculatePaymentAmount = (paymentMethod) => {
    if (!selectedCourse?.pricing) {
      return 'N/A';
    }

    switch (paymentMethod) {
      case 'monthly':
        return selectedCourse.pricing.monthly;
      case 'annual3':
        return selectedCourse.pricing.annual3;
      case 'annual1':
        return selectedCourse.pricing.annual1;
      default:
        return selectedCourse.pricing.monthly;
    }
  };

  const canEnrollInTrialCourse = () => true;

  if (loading) {
    return (
      <Container maxWidth='lg'>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth='lg'>
      <Box sx={{ py: 4 }}>
        {/* Header della sezione */}
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant='h3' 
            sx={{ 
              mb: 2, 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              fontWeight: 700,
              color: 'primary.main',
              borderBottom: '3px solid',
              borderColor: 'primary.main',
              pb: 2
            }}
          >
            <School sx={{ fontSize: 40 }} />
            Lezioni di Prova
          </Typography>
          <Typography variant='h6' color='text.secondary' sx={{ mb: 3 }}>
            Scopri i nostri corsi con lezioni di prova gratuite. Non è richiesta tessera associativa.
          </Typography>
        </Box>

        {/* Modal per risultati operazioni */}
        <ModalResult open={modalOpen} onClose={handleCloseModal} message={success || error} severity={modalType} />

        {/* Lista corsi */}
        {(!courses || courses.length === 0) ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            py: 8,
            flexDirection: 'column',
            gap: 2
          }}>
            <School sx={{ fontSize: 64, color: 'text.secondary' }} />
            <Typography variant='h5' color='text.secondary' textAlign='center'>
              Nessuna lezione di prova disponibile al momento
            </Typography>
            <Typography variant='body1' color='text.secondary' textAlign='center'>
              Controlla più tardi per nuove opportunità
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3} alignItems="stretch">
            {courses.map((course) => (
              <Grid size={{ xs: 12, md: 6, lg: 4 }} key={course._id} sx={{ display: 'flex' }}>
                <CourseCard
                  course={course}
                  enrollment={isEnrolled(course._id)}
                  isSuspendedEnrollment={false}
                  onEnroll={handleEnroll}
                  validMembership
                  canEnrollInCourse={canEnrollInTrialCourse}
                  buttonLabel='Iscriviti alla Prova'
                />
              </Grid>
            ))}
          </Grid>
        )}

        {/* TRIAL LESSONS Enrollment Dialog */}
        <Dialog open={enrollDialogOpen} onClose={() => setEnrollDialogOpen(false)} maxWidth='sm' fullWidth>
          <DialogTitle>Iscriviti alla Lezione di Prova</DialogTitle>
          <DialogContent>
            {selectedCourse && (
              <Box sx={{ mb: 3 }}>
                <Typography variant='h6' gutterBottom>
                  {selectedCourse.name}
                </Typography>
                <Typography variant='body2' color='text.secondary' gutterBottom>
                  {selectedCourse.description}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Sede: {selectedCourse.venue}
                </Typography>
                {selectedCourse.weekdays && selectedCourse.weekdays.length > 0 && (
                  <Typography variant='body2' color='text.secondary'>
                    Giorni: {selectedCourse.weekdays.map(day => {
                      const dayMap = {
                        0: 'Dom',
                        1: 'Lun',
                        2: 'Mar',
                        3: 'Mer',
                        4: 'Gio',
                        5: 'Ven',
                        6: 'Sab'
                      };
                      return dayMap[day] || day;
                    }).join(', ')}
                  </Typography>
                )}
                {selectedCourse.time && (
                  <Typography variant='body2' color='text.secondary'>
                    Orario: {selectedCourse.time}
                  </Typography>
                )}
                <Typography variant='body2' color='primary.main' sx={{ fontWeight: 'bold', mt: 1 }}>
                  ✅ Lezione di prova - Non richiede tessera associativa
                </Typography>
                {selectedCourse.startDate && (
                  <Typography variant='body2' color='text.secondary'>
                    Inizio: {dayjs(selectedCourse.startDate).locale('it').format('DD-MMM-YY')}
                  </Typography>
                )}
                {selectedCourse.endDate && (
                  <Typography variant='body2' color='text.secondary'>
                    Fine: {dayjs(selectedCourse.endDate).locale('it').format('DD-MMM-YY')}
                  </Typography>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEnrollDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleEnrollSubmit} variant='contained'>
              Conferma Iscrizione
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default TrialLessons;
